import React, { useState, useMemo, useCallback } from "react";
import { RELATIONSHIP_OPTIONS } from "../../../../constants/membersConstants";
import { isBeneficiaryShareTotalOver100 } from "../../../../api/updateHouseholdAPI";
import ShareSlider, { SharePresetButtons } from "../../../ShareSlider";
import { useRelationshipPrefillMany } from "../../../../hooks/useRelationshipPrefill";

const isBlank = (val) => val === null || val === undefined || val === "";

const getMemberName = (members, memberId) => {
  const m = members?.find((x) => x.id === memberId);
  if (!m?.personal_info) return "Member";
  const fn = m.personal_info.first_name || "";
  const ln = m.personal_info.last_name || "";
  return `${fn} ${ln}`.trim() || "Member";
};

const fieldMissing = (b) => {
  const shareNum = parseFloat(b?.share);
  const shareIncomplete =
    b?.share === null || b?.share === undefined || b?.share === "" || !Number.isFinite(shareNum);
  return {
    household_member_id: isBlank(b?.household_member_id),
    type: isBlank(b?.type),
    share: shareIncomplete,
    relationship: isBlank(b?.relationship),
  };
};

const BeneficiariesForm = ({
  householdData,
  fieldData,
  entityId,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
  clientToken,
}) => {
  const account = fieldData || {};
  const existingBeneficiaries = useMemo(
    () => Array.isArray(account.beneficiaries) ? account.beneficiaries : [],
    [account.beneficiaries],
  );
  const members = useMemo(() => householdData?.members || [], [householdData]);

  // Owner member IDs — excluded from beneficiary member dropdown
  const ownerMemberIds = useMemo(() => {
    const ids = new Set();
    (account.owners_info || []).forEach((o) => { if (o.id) ids.add(o.id); });
    return ids;
  }, [account.owners_info]);

  const [formData, setFormData] = useState(() =>
    existingBeneficiaries.map((b) => ({
      household_member_id: b.household_member_id || "",
      type: b.type || "",
      share: b.share ?? "",
      relationship: b.relationship || "",
    })),
  );

  const [deletedIds, setDeletedIds] = useState(new Set());
  const [error, setError] = useState("");

  // Count rows with missing fields (excluding deleted)
  const rowsWithMissing = useMemo(() => {
    return existingBeneficiaries
      .map((b, idx) => ({ b, idx, missing: fieldMissing(b) }))
      .filter(({ b, missing }) => !deletedIds.has(b?.id) && Object.values(missing).some(Boolean));
  }, [existingBeneficiaries, deletedIds]);

  const totalMissing = rowsWithMissing.reduce(
    (sum, { missing }) => sum + Object.values(missing).filter(Boolean).length,
    0,
  );

  const handleChange = useCallback((idx, field, value) => {
    setFormData((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    setError("");
  }, []);

  useRelationshipPrefillMany({
    householdId: householdData?.id,
    accountId: entityId,
    clientToken: clientToken || null,
    rows: formData,
    getMemberId: (row) => row?.household_member_id,
    getCurrentValue: (row) => row?.relationship,
    onPrefill: (idx, relationship) => handleChange(idx, "relationship", relationship),
  });

  const handleDelete = useCallback((beneficiaryId) => {
    if (!beneficiaryId) return;
    setDeletedIds((prev) => {
      const next = new Set(prev);
      next.add(beneficiaryId);
      return next;
    });
    setError("");
  }, []);

  const handleUndoDelete = useCallback((beneficiaryId) => {
    setDeletedIds((prev) => {
      const next = new Set(prev);
      next.delete(beneficiaryId);
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    let primaryTotal = 0;
    let contingentTotal = 0;

    for (let i = 0; i < formData.length; i++) {
      const orig = existingBeneficiaries[i];
      if (deletedIds.has(orig?.id)) continue;

      const row = formData[i];
      const hm = row.household_member_id || orig?.household_member_id;
      const typ = row.type || orig?.type;
      const shareRaw = row.share !== "" ? row.share : orig?.share;
      const rel = row.relationship || orig?.relationship;
      const shareNum = parseFloat(shareRaw);

      if (isBlank(hm) || isBlank(typ) || !Number.isFinite(shareNum) || isBlank(rel)) {
        setError("Complete member, type, share %, and relationship for each beneficiary row before saving.");
        return false;
      }

      const t = String(typ).toLowerCase();
      if (t === "primary") primaryTotal += shareNum;
      if (t === "contingent") contingentTotal += shareNum;
    }

    if (isBeneficiaryShareTotalOver100(primaryTotal)) {
      setError(`Primary allocations cannot exceed 100%. Current total: ${Math.round(primaryTotal * 100) / 100}%.`);
      return false;
    }
    if (isBeneficiaryShareTotalOver100(contingentTotal)) {
      setError(`Contingent allocations cannot exceed 100%. Current total: ${Math.round(contingentTotal * 100) / 100}%.`);
      return false;
    }

    return true;
  }, [formData, existingBeneficiaries, deletedIds]);

  const buildPayload = useCallback(() => {
    const toUpdate = [];
    const toAdd = [];
    const toDelete = [];

    for (let i = 0; i < existingBeneficiaries.length; i++) {
      const orig = existingBeneficiaries[i];

      // Deleted
      if (deletedIds.has(orig?.id)) {
        toDelete.push(orig.id);
        continue;
      }

      const row = formData[i] || {};
      const household_member_id = row.household_member_id || orig.household_member_id;
      const type = row.type || orig.type;
      const shareRaw = row.share !== "" ? row.share : orig.share;
      const share = shareRaw !== null && shareRaw !== undefined && shareRaw !== ""
        ? parseFloat(shareRaw)
        : null;
      const relationship = row.relationship || orig.relationship;

      const isNew = !orig.id || orig.__isNew;
      if (isNew) {
        if (household_member_id) {
          toAdd.push({ household_member_id, type, share, relationship });
        }
      } else {
        toUpdate.push({ id: orig.id, household_member_id, type, share, relationship });
      }
    }

    const beneficiariesPayload = {};
    if (toAdd.length > 0) {
      beneficiariesPayload.create = toAdd.map((row) => ({
        household_member_id: row.household_member_id,
        type: String(row.type || "").toLowerCase(),
        share: row.share,
        relationship: row.relationship,
      }));
    }
    if (toUpdate.length > 0) {
      beneficiariesPayload.update = {};
      for (const row of toUpdate) {
        beneficiariesPayload.update[row.id] = {
          type: row.type,
          share: row.share,
          relationship: row.relationship,
        };
      }
    }
    if (toDelete.length > 0) {
      beneficiariesPayload.delete = toDelete;
    }

    return {
      accounts: {
        [entityId]: Object.keys(beneficiariesPayload).length > 0
          ? { beneficiaries: beneficiariesPayload }
          : {},
      },
    };
  }, [existingBeneficiaries, formData, entityId, deletedIds]);

  const handleSubmit = (cb) => {
    if (!validate()) return;
    cb(buildPayload());
  };

  if (existingBeneficiaries.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">No beneficiaries on this account.</p>
      </div>
    );
  }

  if (rowsWithMissing.length === 0 && deletedIds.size === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All beneficiary details have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        {totalMissing > 0 && (
          <span className="text-sm text-red-500 font-medium">
            ● {totalMissing} Required field{totalMissing !== 1 ? "s" : ""} missing
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Review all beneficiaries below. Fields highlighted in red are missing and need to be filled.
        You can also remove a beneficiary if needed.
      </p>

      {error && (
        <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {existingBeneficiaries.map((b, idx) => {
        const row = formData[idx] || {};
        const isDeleted = deletedIds.has(b?.id);
        const missing = fieldMissing(b);
        const hasMissing = Object.values(missing).some(Boolean);
        const memberName = getMemberName(members, b?.household_member_id || row.household_member_id);

        if (isDeleted) {
          return (
            <div
              key={b?.id || `bene-${idx}`}
              className="bg-red-50/50 border border-red-200 rounded-xl overflow-hidden shadow-sm mb-4 opacity-60"
            >
              <div className="px-4 py-3 flex justify-between items-center">
                <h3 className="text-sm font-bold text-gray-500 flex items-center gap-2 line-through">
                  <span className="bg-gray-200 text-gray-500 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                    {idx + 1}
                  </span>
                  {memberName}
                </h3>
                <button
                  type="button"
                  onClick={() => handleUndoDelete(b.id)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Undo
                </button>
              </div>
            </div>
          );
        }

        return (
          <div
            key={b?.id || `bene-${idx}`}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4"
          >
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="bg-amber-100 text-amber-700 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  {idx + 1}
                </span>
                {memberName}
              </h3>
              <div className="flex items-center gap-2">
                {hasMissing ? (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-wider border border-red-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    Details Required
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wider border border-emerald-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    Complete
                  </span>
                )}
                {b?.id && (
                  <button
                    type="button"
                    onClick={() => handleDelete(b.id)}
                    className="text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                    title="Remove beneficiary"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Member */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Household Member {missing.household_member_id && <span className="text-red-500">*</span>}
                </label>
                {missing.household_member_id ? (
                  <select
                    value={row.household_member_id || ""}
                    onChange={(e) => handleChange(idx, "household_member_id", e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    <option value="">Select member</option>
                    {members.map((m) => {
                      if (ownerMemberIds.has(m.id)) return null;
                      const takenByOther = existingBeneficiaries.some(
                        (row2, j) => j !== idx && row2?.household_member_id === m.id,
                      );
                      if (takenByOther && m.id !== b?.household_member_id) return null;
                      const fn = m.personal_info?.first_name || "";
                      const ln = m.personal_info?.last_name || "";
                      const label = `${fn} ${ln}`.trim() || "Member";
                      return (
                        <option key={m.id} value={m.id}>{label}</option>
                      );
                    })}
                  </select>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {memberName}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Type {missing.type && <span className="text-red-500">*</span>}
                  </label>
                  {missing.type ? (
                    <select
                      value={row.type || ""}
                      onChange={(e) => handleChange(idx, "type", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    >
                      <option value="">Select type</option>
                      <option value="primary">Primary</option>
                      <option value="contingent">Contingent</option>
                    </select>
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 capitalize">
                      {b.type}
                    </div>
                  )}
                </div>

                {/* Share */}
                <div>
                  {missing.share ? (
                    <>
                      <div className="flex items-center gap-3 mb-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Share (%) <span className="text-red-500">*</span>
                        </label>
                        <SharePresetButtons
                          value={parseFloat(row.share) || 0}
                          onChange={(val) => handleChange(idx, "share", val)}
                        />
                      </div>
                      <ShareSlider
                        value={parseFloat(row.share) || 0}
                        onChange={(val) => handleChange(idx, "share", val)}
                      />
                    </>
                  ) : (
                    <>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Share (%)
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                        {b.share}%
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Relationship to Account Owner {missing.relationship && <span className="text-red-500">*</span>}
                </label>
                {missing.relationship ? (
                  <select
                    value={row.relationship || ""}
                    onChange={(e) => handleChange(idx, "relationship", e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    <option value="">Select relationship to account owner</option>
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                    {b.relationship}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleSubmit(onSaveAndExit)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Save & Exit
        </button>
        <button
          onClick={() => handleSubmit(onSave)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {nextLabel || "Save & Next \u2192"}
        </button>
      </div>
    </div>
  );
};

export default BeneficiariesForm;
